import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import FmdGoodRoundedIcon from '@mui/icons-material/FmdGoodRounded';
import LocalActivityOutlinedIcon from '@mui/icons-material/LocalActivityOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    Divider,
    IconButton,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Elements, ExpressCheckoutElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {
    loadStripe,
    Stripe,
    StripeExpressCheckoutElementConfirmEvent,
    StripeExpressCheckoutElementOptions,
    StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js';
import dayjs from 'dayjs';
import {
    createUserWithEmailAndPassword,
    EmailAuthProvider,
    linkWithCredential,
    onAuthStateChanged,
    signInAnonymously,
    signInWithEmailAndPassword,
    User,
} from 'firebase/auth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import bailaLogo from '../../assets/logo.png';
import { useFirebase } from '../../services/context/authContext';
import isDev from '../../util/checkDev';
import {
    createPaymentSheet,
    fetchCheckoutFeatureFlags,
    fetchPaymentQuote,
    fetchPublicPaymentQuote,
    getPublicEvent,
    getPublicUserData,
    lookupEmailAccountStatus,
    mergeCheckoutUsers,
    preflightEmailAddress,
    saveCheckoutAttemptMarker,
    updateCheckoutUserContact,
} from '../../services/publicCheckout';
import {
    CheckoutFeatureFlags,
    EventData,
    PaymentPricingResponse,
    PaymentSheetParamsResponse,
    PublicUserData,
    Ticket,
    TicketSelection,
} from '../../util/types';
import { checkoutPalette } from './checkoutTheme';

type WorkflowState =
    | 'unauthenticated_browsing'
    | 'email_entry'
    | 'email_lookup_pending'
    | 'email_login'
    | 'email_register'
    | 'email_auth_pending'
    | 'registered_missing_contact'
    | 'guest_contact_collecting'
    | 'guest_auth_creating'
    | 'guest_ready'
    | 'registered_ready'
    | 'checkout_in_progress'
    | 'checkout_success'
    | 'checkout_failed';

type EmailMode = 'login' | 'register';

type ContactFormState = {
    email: string;
    name: string;
    lastName: string;
};

type EmailAuthFormState = {
    email: string;
    emailConfirmation: string;
    password: string;
    confirmPassword: string;
};

type ExpressCheckoutState = 'checking' | 'available' | 'unavailable';

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const normalizeErrorMessage = (error: any, fallback: string) => {
    if (!error) {
        return fallback;
    }
    if (typeof error === 'string' && error.trim()) {
        return error;
    }
    if (error?.data?.error && typeof error.data.error === 'string') {
        return error.data.error;
    }
    if (error?.message && typeof error.message === 'string') {
        return error.message;
    }
    return fallback;
};

const formatCurrency = (value?: number) => {
    if (typeof value !== 'number') {
        return '0,00 EUR';
    }

    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
    }).format(value / 100);
};

const calculateStripeFee = (totalAmountWithServiceFee: number): number => {
    if (totalAmountWithServiceFee === 0) {
        return 0;
    }

    const stripeFee = (totalAmountWithServiceFee * 0.015) + 0.25;
    return Math.round(stripeFee * 100) / 100;
};

const normalizeEventAddress = (rawAddress?: string | null): string => {
    if (typeof rawAddress !== 'string') {
        return '';
    }

    return rawAddress.replace(/,?\s*Germany\s*$/i, '').trim();
};

const getFirebaseProviderIds = (user: User | null): string[] => {
    if (!user) {
        return [];
    }

    return user.providerData
        .map((entry) => entry.providerId)
        .filter((entry): entry is string => Boolean(entry));
};

const buildInitialsFallback = (displayName: string | null | undefined) => {
    if (!displayName) {
        return { name: '', lastName: '' };
    }

    const parts = displayName.trim().split(/\s+/);
    return {
        name: parts[0] ?? '',
        lastName: parts.slice(1).join(' '),
    };
};

const isTicketSellable = (ticket: Ticket) => {
    if (!ticket.eventTicketId) {
        return false;
    }
    if (ticket.ticketTypeName === 'GUESTLIST') {
        return false;
    }
    if (typeof ticket.ticketsAvailable === 'number' && ticket.ticketsAvailable <= 0) {
        return false;
    }
    if (ticket.ticketBuyableUntil && dayjs(ticket.ticketBuyableUntil).isBefore(dayjs())) {
        return false;
    }
    return true;
};

const getRemainingTimeLabel = (raw?: string) => {
    if (!raw) {
        return null;
    }

    const untilMs = Date.parse(raw);
    if (Number.isNaN(untilMs)) {
        return null;
    }

    const diffMs = untilMs - Date.now();
    if (diffMs <= 0) {
        return 'Verkauf endet heute';
    }

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours < 24) {
        return `Verkauf endet in ${diffHours} Std.`;
    }

    if (diffHours < 72) {
        const diffDays = Math.ceil(diffHours / 24);
        return `Verkauf endet in ${diffDays} Tagen`;
    }

    return null;
};

const PaymentStep = ({
    clientSecret,
    stripePromise,
    totalAmount,
    onSuccess,
    onError,
    successUrl,
}: {
    clientSecret: string;
    stripePromise: Promise<Stripe | null>;
    totalAmount?: number;
    onSuccess: () => void;
    onError: (message: string) => void;
    successUrl: string;
}) => {
    return (
        <Elements
            stripe={stripePromise}
            options={{
                clientSecret,
                appearance: {
                    theme: 'night',
                    variables: {
                        colorPrimary: checkoutPalette.accent,
                        colorBackground: checkoutPalette.panelSurfaceStrong,
                        colorText: checkoutPalette.secondary,
                        colorDanger: checkoutPalette.danger,
                        borderRadius: '14px',
                    },
                },
            }}
        >
            <PaymentForm
                totalAmount={totalAmount}
                onSuccess={onSuccess}
                onError={onError}
                successUrl={successUrl}
            />
        </Elements>
    );
};

const PaymentForm = ({
    totalAmount,
    onSuccess,
    onError,
    successUrl,
}: {
    totalAmount?: number;
    onSuccess: () => void;
    onError: (message: string) => void;
    successUrl: string;
}) => {
    const theme = useTheme();
    const mobileAppStrip = useMediaQuery(theme.breakpoints.down('sm'));
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [expressCheckoutState, setExpressCheckoutState] = useState<ExpressCheckoutState>('checking');
    const alwaysShowPaymentElementInDev = isDev();

    const confirmCurrentPayment = useCallback(async (expressEvent?: StripeExpressCheckoutElementConfirmEvent) => {
        if (!stripe || !elements || submitting) {
            return;
        }

        setSubmitting(true);

        const { error: submitError } = await elements.submit();
        if (submitError) {
            const submitMessage = submitError.message ?? 'Die Zahlungsdaten sind unvollständig.';
            expressEvent?.paymentFailed({
                reason: 'fail',
                message: submitMessage,
            });
            onError(submitMessage);
            setSubmitting(false);
            return;
        }

        const result = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: successUrl,
            },
            redirect: 'if_required',
        });

        if (result.error) {
            const paymentMessage = result.error.message ?? 'Die Zahlungsbestätigung ist fehlgeschlagen.';
            expressEvent?.paymentFailed({
                reason: 'fail',
                message: paymentMessage,
            });
            onError(paymentMessage);
            setSubmitting(false);
            return;
        }

        if (result.paymentIntent?.status === 'succeeded' || result.paymentIntent?.status === 'processing') {
            onSuccess();
        }

        setSubmitting(false);
    }, [elements, onError, onSuccess, stripe, submitting, successUrl]);

    const handleExpressCheckoutReady = useCallback((event: StripeExpressCheckoutElementReadyEvent) => {
        const hasAvailablePaymentMethod = Boolean(
            event.availablePaymentMethods
            && Object.values(event.availablePaymentMethods).some(Boolean)
        );
        setExpressCheckoutState(hasAvailablePaymentMethod ? 'available' : 'unavailable');
    }, []);

    const expressCheckoutOptions = useMemo<StripeExpressCheckoutElementOptions>(() => ({
        business: {
            name: 'BAILA',
        },
        buttonHeight: 52,
        buttonTheme: {
            applePay: 'white-outline',
            googlePay: 'white',
            paypal: 'white',
        },
        buttonType: {
            applePay: 'check-out',
            googlePay: 'checkout',
            paypal: 'checkout',
        },
        layout: {
            maxColumns: 1,
            overflow: 'never',
        },
        paymentMethodOrder: ['apple_pay', 'google_pay', 'link', 'paypal'],
        paymentMethods: {
            applePay: 'always',
            googlePay: 'auto',
            link: 'auto',
            paypal: 'auto',
        },
    }), []);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        await confirmCurrentPayment();
    };

    const showExpressCheckout = expressCheckoutState === 'available';
    const showPaymentElement = mobileAppStrip || alwaysShowPaymentElementInDev || expressCheckoutState === 'unavailable';

    return (
        <Box component="form" onSubmit={handleSubmit}>
            {showExpressCheckout ? (
                <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                    <Typography fontWeight={800}>
                        Apple Pay und Express-Checkout
                    </Typography>
                    <Box
                        sx={{
                            borderRadius: 3,
                            border: `1px solid ${checkoutPalette.border}`,
                            backgroundColor: 'rgba(255,255,255,0.02)',
                            p: 1.25,
                        }}
                    >
                        <ExpressCheckoutElement
                            options={expressCheckoutOptions}
                            onReady={handleExpressCheckoutReady}
                            onLoadError={() => setExpressCheckoutState('unavailable')}
                            onConfirm={confirmCurrentPayment}
                        />
                    </Box>
                    {showPaymentElement ? (
                        <Divider sx={{ borderColor: checkoutPalette.border }}>
                            <Typography variant="body2" color={checkoutPalette.textMuted}>
                                oder mit Karte bezahlen
                            </Typography>
                        </Divider>
                    ) : null}
                </Stack>
            ) : null}

            {expressCheckoutState === 'checking' ? (
                <Box
                    sx={{
                        minHeight: 96,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Stack spacing={1.5} alignItems="center">
                        <CircularProgress size={22} sx={{ color: checkoutPalette.secondary }} />
                        <Typography variant="body2" color={checkoutPalette.textMuted}>
                            Express-Checkout-Optionen werden geprüft...
                        </Typography>
                    </Stack>
                </Box>
            ) : null}

            {showPaymentElement ? (
                <>
                    <PaymentElement />
                    <Button
                        type="submit"
                        disabled={!stripe || !elements || submitting}
                        variant="contained"
                        fullWidth
                        sx={{
                            mt: 3,
                            borderRadius: 2.5,
                            py: 1.35,
                            textTransform: 'none',
                            fontWeight: 900,
                            fontSize: '1.05rem',
                            color: checkoutPalette.primary,
                            bgcolor: checkoutPalette.secondary,
                            boxShadow: 'none',
                            '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.92)',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        {submitting || typeof totalAmount !== 'number'
                            ? <CircularProgress size={22} sx={{ color: checkoutPalette.primary }} />
                            : `Jetzt ${formatCurrency(totalAmount)} bezahlen`}
                    </Button>
                </>
            ) : null}

            {expressCheckoutState === 'checking' ? (
                <Box
                    sx={{
                        position: 'absolute',
                        width: 1,
                        height: 1,
                        overflow: 'hidden',
                        opacity: 0,
                        pointerEvents: 'none',
                    }}
                >
                    <ExpressCheckoutElement
                        options={expressCheckoutOptions}
                        onReady={handleExpressCheckoutReady}
                        onLoadError={() => setExpressCheckoutState('unavailable')}
                        onConfirm={confirmCurrentPayment}
                    />
                </Box>
            ) : null}
        </Box>
    );
};

export default function PublicEventCheckoutPage() {
    const { eventId = '' } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const fullScreenDialog = useMediaQuery(theme.breakpoints.down('md'));
    const mobileAppStrip = useMediaQuery(theme.breakpoints.down('sm'));
    const { auth } = useFirebase();
    const checkoutRef = useRef<HTMLDivElement | null>(null);

    const refBy = searchParams.get('refBy');

    const [event, setEvent] = useState<EventData | null>(null);
    const [eventLoading, setEventLoading] = useState(true);
    const [eventError, setEventError] = useState<string | null>(null);

    const [workflowState, setWorkflowState] = useState<WorkflowState>('unauthenticated_browsing');
    const [authDialogOpen, setAuthDialogOpen] = useState(false);
    const [authBusy, setAuthBusy] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);
    const [checkoutError, setCheckoutError] = useState<string | null>(null);

    const [selectedQuantities, setSelectedQuantities] = useState<Record<string, number>>({});

    const [firebaseUser, setFirebaseUser] = useState<User | null>(auth.currentUser);
    const [publicUser, setPublicUser] = useState<PublicUserData | null>(null);
    const [profileLoading, setProfileLoading] = useState(Boolean(auth.currentUser));

    const [guestForm, setGuestForm] = useState<ContactFormState>({ email: '', name: '', lastName: '' });
    const [contactForm, setContactForm] = useState<ContactFormState>({ email: '', name: '', lastName: '' });
    const [emailMode, setEmailMode] = useState<EmailMode>('login');
    const [emailAuthForm, setEmailAuthForm] = useState<EmailAuthFormState>({
        email: '',
        emailConfirmation: '',
        password: '',
        confirmPassword: '',
    });

    const [featureFlags, setFeatureFlags] = useState<CheckoutFeatureFlags | null>(null);
    const [paymentQuote, setPaymentQuote] = useState<PaymentPricingResponse | null>(null);
    const [paymentSheet, setPaymentSheet] = useState<PaymentSheetParamsResponse | null>(null);
    const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [descriptionExpanded, setDescriptionExpanded] = useState(false);
    const [openTicketDetails, setOpenTicketDetails] = useState<Record<string, boolean>>({});
    const [summaryExpanded, setSummaryExpanded] = useState(false);
    const [ticketPreviewTotals, setTicketPreviewTotals] = useState<Record<string, number>>({});
    const [publicQuoteLoading, setPublicQuoteLoading] = useState(false);

    const fallbackNameParts = useMemo(
        () => buildInitialsFallback(firebaseUser?.displayName),
        [firebaseUser?.displayName]
    );

    const currentUser = useMemo<PublicUserData | null>(() => {
        if (!firebaseUser) {
            return null;
        }

        return {
            uid: firebaseUser.uid,
            email: publicUser?.email ?? firebaseUser.email ?? '',
            providerIds: publicUser?.providerIds ?? getFirebaseProviderIds(firebaseUser),
            name: publicUser?.name ?? fallbackNameParts.name,
            lastName: publicUser?.lastName ?? fallbackNameParts.lastName,
            ticketMigrationStatus: publicUser?.ticketMigrationStatus,
            userProfilePic: publicUser?.userProfilePic,
        };
    }, [fallbackNameParts.lastName, fallbackNameParts.name, firebaseUser, publicUser]);

    const availableTickets = useMemo(
        () => (event?.tickets ?? []).filter(isTicketSellable),
        [event?.tickets]
    );

    const selectedTicketTypes = useMemo<TicketSelection[]>(
        () =>
            availableTickets
                .map((ticket) => ({
                    eventTicketId: ticket.eventTicketId ?? '',
                    quantity: selectedQuantities[ticket.eventTicketId ?? ''] ?? 0,
                }))
                .filter((ticket) => ticket.eventTicketId && ticket.quantity > 0),
        [availableTickets, selectedQuantities]
    );

    const ticketBadgeSource = useMemo(
        () => (event?.tickets ?? []).filter((ticket) => {
            if (!ticket?.eventTicketId) {
                return false;
            }
            if (ticket.ticketTypeName === 'GUESTLIST') {
                return false;
            }
            if (ticket.ticketBuyableUntil && dayjs(ticket.ticketBuyableUntil).isBefore(dayjs())) {
                return false;
            }
            return typeof ticket.ticketTypePrice === 'number';
        }).sort((a, b) => (a.ticketTypePrice ?? 0) - (b.ticketTypePrice ?? 0)),
        [event?.tickets]
    );

    const cheapestAvailableBadgeTicket = useMemo(
        () => ticketBadgeSource.find((ticket) => typeof ticket.ticketsAvailable === 'number' && ticket.ticketsAvailable > 0),
        [ticketBadgeSource]
    );

    const cheapestKnownAvailabilityBadgeTicket = useMemo(
        () => ticketBadgeSource.find((ticket) => typeof ticket.ticketsAvailable === 'number'),
        [ticketBadgeSource]
    );

    const ticketsLeftBadgeCount = useMemo(() => {
        const ticket = cheapestAvailableBadgeTicket ?? cheapestKnownAvailabilityBadgeTicket;
        return typeof ticket?.ticketsAvailable === 'number'
            ? Math.max(ticket.ticketsAvailable, 0)
            : undefined;
    }, [cheapestAvailableBadgeTicket, cheapestKnownAvailabilityBadgeTicket]);

    const showTicketsLeftBadge = typeof ticketsLeftBadgeCount === 'number' && ticketsLeftBadgeCount > 0 && ticketsLeftBadgeCount <= 25;

    const selectedCount = useMemo(
        () => selectedTicketTypes.reduce((sum, ticket) => sum + ticket.quantity, 0),
        [selectedTicketTypes]
    );

    const recommendedTicketId = useMemo(() => {
        const available = availableTickets.filter((ticket) => (ticket.ticketsAvailable ?? 0) > 0);
        if (available.length === 0) {
            return null;
        }

        return available.reduce((previous, current) =>
            (current.ticketTypePrice ?? 0) < (previous.ticketTypePrice ?? 0) ? current : previous
        ).eventTicketId ?? null;
    }, [availableTickets]);

    const fallbackTotalCents = useMemo(() => {
        return availableTickets.reduce((sum, ticket) => {
            const quantity = selectedQuantities[ticket.eventTicketId ?? ''] ?? 0;
            return sum + Math.round((ticket.ticketTypePrice ?? 0) * 100) * quantity;
        }, 0);
    }, [availableTickets, selectedQuantities]);

    const selectedTicketRows = useMemo(() => {
        return selectedTicketTypes
            .map((ticket) => {
                const ticketConfig = availableTickets.find((entry) => entry.eventTicketId === ticket.eventTicketId);
                const basePriceCents = Math.round((ticketConfig?.ticketTypePrice ?? 0) * 100);

                return {
                    ...ticket,
                    ticketConfig,
                    basePriceCents,
                    linePriceCents: basePriceCents * ticket.quantity,
                };
            })
            .filter((ticket) => ticket.ticketConfig);
    }, [availableTickets, selectedTicketTypes]);

    const localSubtotalCents = useMemo(
        () => selectedTicketRows.reduce((sum, ticket) => sum + ticket.linePriceCents, 0),
        [selectedTicketRows]
    );

    const localFeesCents = useMemo(
        () => Math.round(calculateStripeFee(localSubtotalCents / 100) * 100),
        [localSubtotalCents]
    );

    const localTotalCents = useMemo(
        () => localSubtotalCents + localFeesCents,
        [localFeesCents, localSubtotalCents]
    );

    const quotedPricing = paymentSheet?.pricing ?? paymentQuote?.pricing;
    const hasQuotedPricing = Boolean(
        quotedPricing && (
            quotedPricing.totalAmount != null
            || quotedPricing.discountedSubtotal != null
            || quotedPricing.serviceFee != null
            || quotedPricing.stripeFee != null
        )
    );
    const quotedTotalCents = quotedPricing?.totalAmount ?? null;

    const displaySubtotalCents = hasQuotedPricing
        ? quotedPricing?.discountedSubtotal ?? localSubtotalCents
        : localSubtotalCents;
    const displayFeesCents = hasQuotedPricing
        ? ((quotedPricing?.serviceFee ?? 0) + (quotedPricing?.stripeFee ?? 0))
        : localFeesCents;
    const displayTotalCents = hasQuotedPricing
        ? quotedPricing?.totalAmount ?? localTotalCents
        : localTotalCents;
    const promoDiscountCents = paymentQuote?.promoSummary?.discountAmount
        ?? paymentSheet?.promoSummary?.discountAmount
        ?? 0;
    const freeTicketCount = quotedPricing?.freeTicketCount ?? 0;
    const desktopPaymentOpen = !mobileAppStrip && Boolean(paymentSheet?.paymentIntent && stripePromise);
    const desktopPaymentPreparing = !mobileAppStrip && paymentLoading && !desktopPaymentOpen;
    const publicQuotePending = selectedTicketTypes.length > 0 && !hasQuotedPricing && (
        publicQuoteLoading
        || paymentLoading
        || !firebaseUser
    );
    const priceDisplayPending = mobileAppStrip
        ? publicQuotePending
        : (selectedTicketTypes.length > 0 && !hasQuotedPricing);

    const ticketSelectionMaxById = useMemo(() => {
        return availableTickets.reduce<Record<string, number>>((accumulator, ticket) => {
            const ticketId = ticket.eventTicketId;
            if (!ticketId) {
                return accumulator;
            }

            const available = typeof ticket.ticketsAvailable === 'number'
                ? Math.max(ticket.ticketsAvailable, 0)
                : 5;
            accumulator[ticketId] = Math.max(0, Math.min(5, available));
            return accumulator;
        }, {});
    }, [availableTickets]);

    const missingRegisteredContact = Boolean(
        firebaseUser && !firebaseUser.isAnonymous && (
            !currentUser?.email?.trim()
            || !currentUser?.name?.trim()
            || !currentUser?.lastName?.trim()
        )
    );

    const setNextReadyState = useCallback((user: User | null, profile: PublicUserData | null) => {
        if (!user) {
            setWorkflowState('unauthenticated_browsing');
            return;
        }

        const mergedProfile = {
            email: profile?.email ?? user.email ?? '',
            name: profile?.name ?? buildInitialsFallback(user.displayName).name,
            lastName: profile?.lastName ?? buildInitialsFallback(user.displayName).lastName,
        };

        if (user.isAnonymous) {
            setWorkflowState(
                mergedProfile.email && mergedProfile.name && mergedProfile.lastName
                    ? 'guest_ready'
                    : 'guest_contact_collecting'
            );
            return;
        }

        setWorkflowState(
            mergedProfile.email && mergedProfile.name && mergedProfile.lastName
                ? 'registered_ready'
                : 'registered_missing_contact'
        );
    }, []);

    const hydrateProfile = useCallback(async (user: User | null) => {
        if (!user) {
            setPublicUser(null);
            setProfileLoading(false);
            return null;
        }

        setProfileLoading(true);
        try {
            const token = await user.getIdToken();
            const profile = await getPublicUserData(user.uid, token);
            setPublicUser(profile);
            setProfileLoading(false);
            return profile;
        } catch {
            setPublicUser(null);
            setProfileLoading(false);
            return null;
        }
    }, []);

    const syncContactForms = useCallback((profile: PublicUserData | null, user: User | null) => {
        const fallback = buildInitialsFallback(user?.displayName);
        const email = profile?.email ?? user?.email ?? '';
        const name = profile?.name ?? fallback.name;
        const lastName = profile?.lastName ?? fallback.lastName;

        setGuestForm({ email, name, lastName });
        setContactForm({ email, name, lastName });
        setEmailAuthForm((previous) => ({
            ...previous,
            email: previous.email || email,
        }));
    }, []);

    const refreshActiveUser = useCallback(async (user: User | null) => {
        const profile = await hydrateProfile(user);
        syncContactForms(profile, user);
        setNextReadyState(user, profile);
        return profile;
    }, [hydrateProfile, setNextReadyState, syncContactForms]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setFirebaseUser(user);
            await refreshActiveUser(user);
        });

        return unsubscribe;
    }, [auth, refreshActiveUser]);

    useEffect(() => {
        let active = true;

        async function loadEvent() {
            try {
                setEventLoading(true);
                const response = await getPublicEvent(eventId);
                if (!active) {
                    return;
                }
                setEvent(response);
                setEventError(null);
            } catch (error: any) {
                if (!active) {
                    return;
                }
                setEventError(
                    normalizeErrorMessage(
                        error,
                        'Dieses Event ist im Web aktuell noch nicht verfügbar. Der öffentliche Event-Endpunkt muss noch für nicht angemeldete Zugriffe aktiviert werden.'
                    )
                );
            } finally {
                if (active) {
                    setEventLoading(false);
                }
            }
        }

        if (eventId) {
            loadEvent();
        }

        return () => {
            active = false;
        };
    }, [eventId]);

    useEffect(() => {
        if (workflowState === 'checkout_in_progress' && paymentSheet?.paymentIntent) {
            checkoutRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [paymentSheet?.paymentIntent, workflowState]);

    useEffect(() => {
        let active = true;

        if (!eventId || availableTickets.length === 0) {
            setTicketPreviewTotals({});
            return () => {
                active = false;
            };
        }

        Promise.all(
            availableTickets.map(async (ticket) => {
                const ticketId = ticket.eventTicketId ?? '';
                if (!ticketId) {
                    return null;
                }

                try {
                    const quote = await fetchPublicPaymentQuote({
                        eventId,
                        ticketTypes: [{ eventTicketId: ticketId, quantity: 1 }],
                    });

                    return {
                        ticketId,
                        totalAmount: quote.pricing?.totalAmount,
                    };
                } catch {
                    return null;
                }
            })
        ).then((entries) => {
            if (!active) {
                return;
            }

            const nextTotals = entries.reduce<Record<string, number>>((accumulator, entry) => {
                if (entry?.ticketId && typeof entry.totalAmount === 'number') {
                    accumulator[entry.ticketId] = entry.totalAmount;
                }
                return accumulator;
            }, {});

            setTicketPreviewTotals(nextTotals);
        }).catch(() => {
            if (active) {
                setTicketPreviewTotals({});
            }
        });

        return () => {
            active = false;
        };
    }, [availableTickets, eventId]);

    useEffect(() => {
        let active = true;

        if (selectedTicketTypes.length < 1) {
            setPublicQuoteLoading(false);
            setPaymentQuote(null);
            return () => {
                active = false;
            };
        }

        setPublicQuoteLoading(true);
        setPaymentQuote(null);

        const loadQuote = async () => {
            try {
                if (firebaseUser) {
                    const token = await firebaseUser.getIdToken();
                    const quote = await fetchPaymentQuote({
                        eventId,
                        ticketTypes: selectedTicketTypes,
                        userId: firebaseUser.uid,
                        refBy,
                    }, token);

                    if (active) {
                        setPaymentQuote(quote);
                        setPublicQuoteLoading(false);
                    }

                    return;
                }

                const quote = await fetchPublicPaymentQuote({
                    eventId,
                    ticketTypes: selectedTicketTypes,
                });

                if (active) {
                    setPaymentQuote(quote);
                    setPublicQuoteLoading(false);
                }
            } catch {
                if (active) {
                    setPaymentQuote(null);
                    setPublicQuoteLoading(false);
                }
            }
        };

        loadQuote();

        return () => {
            active = false;
        };
    }, [eventId, firebaseUser, refBy, selectedTicketTypes]);

    const resetPreparedCheckout = () => {
        setPaymentQuote(null);
        setPaymentSheet(null);
        setStripePromise(null);
        if (firebaseUser) {
            setNextReadyState(firebaseUser, publicUser);
        } else {
            setWorkflowState('unauthenticated_browsing');
        }
    };

    const updateQuantity = (ticketId: string, nextValue: number) => {
        const maxQuantity = ticketSelectionMaxById[ticketId] ?? 5;
        setSelectedQuantities((previous) => ({
            ...previous,
            [ticketId]: Math.max(0, Math.min(maxQuantity, nextValue)),
        }));
        setCheckoutError(null);
        if (paymentSheet || paymentQuote) {
            resetPreparedCheckout();
        }
    };

    const ensureTicketSelection = useCallback(() => {
        if (selectedTicketTypes.length > 0) {
            return true;
        }

        setCheckoutError('Bitte wähle mindestens ein Ticket aus, bevor du fortfährst.');
        return false;
    }, [selectedTicketTypes]);

    const validateGuestContactForm = async (values: ContactFormState) => {
        if (!values.name.trim() || !values.lastName.trim() || !values.email.trim()) {
            throw new Error('Bitte fülle E-Mail, Vorname und Nachname aus.');
        }
        if (!emailRegex.test(values.email.trim().toLowerCase())) {
            throw new Error('Bitte gib eine gültige E-Mail-Adresse ein.');
        }

        const preflight = await preflightEmailAddress(values.email);
        if (preflight.status === 'invalid') {
            throw new Error(preflight.message ?? 'Diese E-Mail-Adresse kann nicht verwendet werden.');
        }
    };

    const validateRegisteredContactForm = (values: ContactFormState) => {
        if (!values.name.trim() || !values.lastName.trim()) {
            throw new Error('Bitte fülle Vorname und Nachname aus.');
        }
    };

    const beginCheckout = useCallback(async (userOverride?: User | null) => {
        const activeUser = userOverride ?? auth.currentUser;
        if (!activeUser) {
            setAuthDialogOpen(true);
            setWorkflowState('email_entry');
            return;
        }

        if (!ensureTicketSelection()) {
            return;
        }

        setPaymentLoading(true);
        setCheckoutError(null);

        try {
            saveCheckoutAttemptMarker({
                eventId,
                userId: activeUser.uid,
                ticketCount: selectedTicketTypes.reduce((sum, ticket) => sum + ticket.quantity, 0),
                startedAt: Date.now(),
                ticketTypes: selectedTicketTypes,
            });

            const token = await activeUser.getIdToken();
            const payload = {
                eventId,
                ticketTypes: selectedTicketTypes,
                userId: activeUser.uid,
                refBy,
            };

            try {
                const flags = await fetchCheckoutFeatureFlags(token);
                setFeatureFlags(flags);
            } catch {
                setFeatureFlags(null);
            }

            const quote = await fetchPaymentQuote(payload, token);
            const sheet = await createPaymentSheet(payload, token);
            setPaymentQuote(quote);
            setPaymentSheet(sheet);

            if (sheet.mode === 'free') {
                setWorkflowState('checkout_success');
                navigate(`/events/${eventId}/success`);
                return;
            }

            if (!sheet.paymentIntent || !sheet.publishableKey) {
                throw new Error('Die Checkout-Initialisierung hat unvollständige Stripe-Daten zurückgegeben.');
            }

            setStripePromise(loadStripe(sheet.publishableKey));
            setWorkflowState('checkout_in_progress');
            setAuthDialogOpen(false);
            if (mobileAppStrip) {
                setPaymentDialogOpen(true);
            }
        } catch (error: any) {
            setWorkflowState('checkout_failed');
            setCheckoutError(
                normalizeErrorMessage(
                    error,
                    'Der Kauf konnte gerade nicht vorbereitet werden. Bitte versuche es erneut.'
                )
            );
        } finally {
            setPaymentLoading(false);
        }
    }, [auth.currentUser, ensureTicketSelection, eventId, mobileAppStrip, navigate, refBy, selectedTicketTypes]);

    const persistContactForUser = useCallback(async (user: User, values: ContactFormState) => {
        const token = await user.getIdToken();
        await updateCheckoutUserContact({
            uid: user.uid,
            email: values.email.trim().toLowerCase(),
            name: values.name.trim(),
            lastName: values.lastName.trim(),
        }, token);
    }, []);

    const finalizeProviderUpgrade = useCallback(async (
        user: User,
        existingGuestContact?: ContactFormState
    ) => {
        if (existingGuestContact?.email && existingGuestContact?.name && existingGuestContact?.lastName) {
            await persistContactForUser(user, existingGuestContact);
        }

        const profile = await refreshActiveUser(user);
        if (
            !(profile?.email ?? user.email)
            || !(profile?.name ?? buildInitialsFallback(user.displayName).name)
            || !(profile?.lastName ?? buildInitialsFallback(user.displayName).lastName)
        ) {
            setWorkflowState('registered_missing_contact');
            setAuthDialogOpen(true);
            return;
        }

        setAuthDialogOpen(false);
        setWorkflowState('registered_ready');
        await beginCheckout(user);
    }, [beginCheckout, persistContactForUser, refreshActiveUser]);

    const handleGuestSubmit = async () => {
        setAuthBusy(true);
        setAuthError(null);
        setWorkflowState('guest_auth_creating');

        try {
            await validateGuestContactForm(guestForm);

            let guestUser = auth.currentUser;
            if (!guestUser) {
                const credentials = await signInAnonymously(auth);
                guestUser = credentials.user;
            }

            if (!guestUser) {
                throw new Error('Für den Gastkauf konnte keine Benutzersitzung erstellt werden.');
            }

            await persistContactForUser(guestUser, guestForm);
            await refreshActiveUser(guestUser);
            setAuthDialogOpen(false);
            setWorkflowState('guest_ready');
            await beginCheckout(guestUser);
        } catch (error: any) {
            setWorkflowState('guest_contact_collecting');
            setAuthError(
                normalizeErrorMessage(
                    error,
                    'Der Gastkauf konnte nicht vorbereitet werden. Bitte versuche es erneut.'
                )
            );
        } finally {
            setAuthBusy(false);
        }
    };

    const handleRegisteredContactSubmit = async () => {
        if (!auth.currentUser) {
            return;
        }

        setAuthBusy(true);
        setAuthError(null);

        try {
            validateRegisteredContactForm(contactForm);
            const resolvedEmail = contactForm.email.trim() || auth.currentUser.email?.trim() || currentUser?.email?.trim() || '';
            if (!resolvedEmail) {
                throw new Error('Die E-Mail-Adresse fehlt. Bitte starte den Checkout erneut.');
            }

            await persistContactForUser(auth.currentUser, {
                ...contactForm,
                email: resolvedEmail,
            });
            await refreshActiveUser(auth.currentUser);
            setAuthDialogOpen(false);
            setWorkflowState('registered_ready');
            await beginCheckout(auth.currentUser);
        } catch (error: any) {
            setWorkflowState('registered_missing_contact');
            setAuthError(
                normalizeErrorMessage(
                    error,
                    'Kontaktdaten konnten nicht gespeichert werden.'
                )
            );
        } finally {
            setAuthBusy(false);
        }
    };

    const handleEmailEntrySubmit = async () => {
        setAuthBusy(true);
        setAuthError(null);
        setWorkflowState('email_lookup_pending');

        try {
            const normalizedEmail = emailAuthForm.email.trim().toLowerCase();
            if (!emailRegex.test(normalizedEmail)) {
                throw new Error('Bitte gib eine gültige E-Mail-Adresse ein.');
            }

            const preflight = await preflightEmailAddress(normalizedEmail);
            if (preflight.status === 'invalid') {
                throw new Error(preflight.message ?? 'Diese E-Mail-Adresse kann nicht verwendet werden.');
            }

            const emailStatus = await lookupEmailAccountStatus(preflight.normalizedEmail ?? normalizedEmail);
            if (!emailStatus.ok || emailStatus.status === 'error') {
                throw new Error(emailStatus.message ?? 'E-Mail-Status konnte gerade nicht geprüft werden.');
            }
            if (emailStatus.exists && !(emailStatus.providerIds ?? []).includes('password')) {
                throw new Error('Dieses Konto verwendet aktuell keinen Passwort-Login. Der Web-Kauf unterstützt derzeit nur E-Mail-Anmeldung und Gastkauf.');
            }

            const nextEmail = emailStatus.normalizedEmail ?? preflight.normalizedEmail ?? normalizedEmail;
            const nextMode: EmailMode = emailStatus.exists ? 'login' : 'register';

            setEmailMode(nextMode);
            setEmailAuthForm((previous) => ({
                ...previous,
                email: nextEmail,
                emailConfirmation: nextMode === 'register' ? previous.emailConfirmation || '' : '',
                password: '',
                confirmPassword: '',
            }));
            setWorkflowState(nextMode === 'login' ? 'email_login' : 'email_register');
        } catch (error: any) {
            setWorkflowState('email_entry');
            setAuthError(
                normalizeErrorMessage(
                    error,
                    'E-Mail konnte gerade nicht geprüft werden. Bitte versuche es erneut.'
                )
            );
        } finally {
            setAuthBusy(false);
        }
    };

    const handleEmailAuthSubmit = async () => {
        setAuthBusy(true);
        setAuthError(null);
        setWorkflowState('email_auth_pending');

        try {
            const normalizedEmail = emailAuthForm.email.trim().toLowerCase();
            if (!emailRegex.test(normalizedEmail)) {
                throw new Error('Bitte gib eine gültige E-Mail-Adresse ein.');
            }
            if (!emailAuthForm.password) {
                throw new Error('Bitte gib dein Passwort ein.');
            }

            const anonymousUser = auth.currentUser?.isAnonymous ? auth.currentUser : null;
            const existingGuestContact = anonymousUser ? guestForm : undefined;
            let nextUser: User | null = null;

            if (emailMode === 'register') {
                if (emailAuthForm.emailConfirmation.trim().toLowerCase() !== normalizedEmail) {
                    throw new Error('Die E-Mail-Bestätigung stimmt nicht überein.');
                }
                if (emailAuthForm.password.length < 6) {
                    throw new Error('Das Passwort muss mindestens 6 Zeichen lang sein.');
                }
                if (emailAuthForm.password !== emailAuthForm.confirmPassword) {
                    throw new Error('Die Passwörter stimmen nicht überein.');
                }

                const preflight = await preflightEmailAddress(normalizedEmail);
                if (preflight.status === 'invalid') {
                    throw new Error(preflight.message ?? 'Diese E-Mail-Adresse kann nicht verwendet werden.');
                }

                if (anonymousUser) {
                    const credential = EmailAuthProvider.credential(normalizedEmail, emailAuthForm.password);
                    const result = await linkWithCredential(anonymousUser, credential);
                    nextUser = result.user;
                } else {
                    const result = await createUserWithEmailAndPassword(auth, normalizedEmail, emailAuthForm.password);
                    nextUser = result.user;
                }
            } else {
                const result = await signInWithEmailAndPassword(auth, normalizedEmail, emailAuthForm.password);
                nextUser = result.user;

                if (anonymousUser && anonymousUser.uid !== nextUser.uid) {
                    const token = await nextUser.getIdToken();
                    await mergeCheckoutUsers({
                        fromUid: anonymousUser.uid,
                        toUid: nextUser.uid,
                        token,
                    });
                }
            }

            if (!nextUser) {
                throw new Error('E-Mail-Authentifizierung konnte nicht abgeschlossen werden.');
            }

            await finalizeProviderUpgrade(nextUser, existingGuestContact);
        } catch (error: any) {
            const authCode = error?.code as string | undefined;
            let message = normalizeErrorMessage(error, 'E-Mail-Authentifizierung ist fehlgeschlagen.');
            if (authCode === 'auth/wrong-password') {
                message = 'Das Passwort ist falsch.';
            } else if (authCode === 'auth/user-not-found') {
                message = 'Für diese E-Mail existiert noch kein Konto.';
            } else if (authCode === 'auth/email-already-in-use') {
                message = 'Diese E-Mail wird bereits verwendet.';
            }
            setWorkflowState(emailMode === 'register' ? 'email_register' : 'email_login');
            setAuthError(message);
        } finally {
            setAuthBusy(false);
        }
    };

    const handleContinue = async () => {
        if (!ensureTicketSelection()) {
            return;
        }

        setCheckoutError(null);

        if (!firebaseUser) {
            setAuthDialogOpen(true);
            setWorkflowState('email_entry');
            return;
        }

        if (firebaseUser.isAnonymous) {
            if (!currentUser?.email || !currentUser?.name || !currentUser?.lastName) {
                setAuthDialogOpen(true);
                setWorkflowState('guest_contact_collecting');
                return;
            }
            await beginCheckout(firebaseUser);
            return;
        }

        if (missingRegisteredContact) {
            setAuthDialogOpen(true);
            setWorkflowState('registered_missing_contact');
            return;
        }

        await beginCheckout(firebaseUser);
    };

    const paymentTotal = quotedTotalCents ?? (priceDisplayPending ? undefined : displayTotalCents);
    const eventDateLabel = event?.startTime ? dayjs(event.startTime).format('dddd, D. MMMM YYYY • HH:mm') : '';
    const eventDescription = event?.eventDescription?.trim() ?? '';
    const hasLongDescription = eventDescription.length > 220 || eventDescription.includes('\n');
    const mobileDateLabel = event?.startTime ? dayjs(event.startTime).format('ddd, D MMM YYYY') : '';
    const mobileTimeLabel = event?.startTime ? dayjs(event.startTime).format('HH:mm') : '';
    const mobileAddressLabel = normalizeEventAddress(event?.eventAddress) || event?.eventVenueName || 'Veranstaltungsort folgt';
    const successUrl = `${window.location.origin}/events/${eventId}/success`;
    const appOpenHref = `/event?eventId=${eventId}${refBy ? `&refBy=${refBy}` : ''}`;
    const authDialogTitle = workflowState === 'registered_missing_contact'
        ? 'Fast geschafft'
        : 'Wohin sollen wir deine Tickets schicken?';
    const authDialogSubtitle = workflowState === 'registered_missing_contact'
        ? 'Ein paar Angaben fehlen noch, dann kann es weitergehen.'
        : 'Gib deine E-Mail-Adresse ein, um mit dem Kauf fortzufahren.';
    const authFieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: 'rgba(0,0,0,0.35)',
            color: checkoutPalette.secondary,
            '& fieldset': {
                borderColor: '#1F1F1F',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(255,255,255,0.18)',
            },
            '&.Mui-focused fieldset': {
                borderColor: checkoutPalette.accent,
            },
        },
        '& .MuiInputBase-input': {
            py: 1.7,
            px: 1.8,
            fontSize: 16,
        },
        '& .MuiInputBase-input::placeholder': {
            color: '#A1A1AA',
            opacity: 1,
        },
        '& .MuiInputLabel-root': {
            color: '#A1A1AA',
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: checkoutPalette.secondary,
        },
    } as const;
    const authPrimaryButtonSx = {
        borderRadius: '12px',
        py: 1.45,
        textTransform: 'uppercase',
        fontWeight: 900,
        fontSize: '1rem',
        letterSpacing: 0.4,
        color: checkoutPalette.secondary,
        background: checkoutPalette.accent,
        boxShadow: 'none',
        '&:hover': {
            background: '#e78a61',
            boxShadow: 'none',
        },
    } as const;
    const authSecondaryTextButtonSx = {
        alignSelf: 'center',
        textTransform: 'none',
        color: checkoutPalette.secondary,
        fontWeight: 700,
        fontSize: '1rem',
        '&:hover': {
            background: 'transparent',
            color: checkoutPalette.secondary,
        },
    } as const;

    const handleAuthBack = () => {
        if (authBusy) {
            return;
        }

        setAuthError(null);
        if (
            workflowState === 'email_login'
            || workflowState === 'email_register'
            || workflowState === 'email_lookup_pending'
            || workflowState === 'guest_contact_collecting'
            || workflowState === 'guest_auth_creating'
        ) {
            setWorkflowState('email_entry');
            return;
        }

        setAuthDialogOpen(false);
    };

    const handleMobilePrimaryAction = async () => {
        if (paymentSheet?.paymentIntent) {
            if (mobileAppStrip) {
                setPaymentDialogOpen(true);
                return;
            }
            checkoutRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
        }

        await handleContinue();
    };

    if (eventLoading) {
        return (
            <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" bgcolor={checkoutPalette.primary}>
                <CircularProgress />
            </Box>
        );
    }

    if (eventError || !event) {
        return (
            <Box
                minHeight="100vh"
                display="flex"
                alignItems="center"
                justifyContent="center"
                px={3}
                sx={{ background: checkoutPalette.pageGradient }}
            >
                <Stack spacing={2} maxWidth={560}>
                    <Typography variant="h3" fontWeight={800} color={checkoutPalette.secondary}>
                        Web-Checkout nicht verfügbar
                    </Typography>
                    <Alert severity="warning">{eventError}</Alert>
                    <Button
                        variant="contained"
                        href={`/event?eventId=${eventId}`}
                        sx={{ alignSelf: 'flex-start', borderRadius: 999, textTransform: 'none' }}
                    >
                        Stattdessen in der App öffnen
                    </Button>
                </Stack>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: checkoutPalette.pageGradient,
                color: checkoutPalette.secondary,
                py: { xs: 3, md: 6 },
                pb: {
                    xs: mobileAppStrip
                        ? (selectedTicketRows.length > 0 ? '290px' : '140px')
                        : '48px',
                    md: 6,
                },
                px: { xs: 2, md: 4 },
            }}
        >
            <Stack spacing={4} maxWidth={1240} mx="auto">
                {mobileAppStrip ? (
                    <Box
                        sx={{
                            position: 'sticky',
                            top: 10,
                            zIndex: 4,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1.5,
                                borderRadius: 4,
                                px: 2,
                                py: 1.5,
                                border: `1px solid ${checkoutPalette.borderStrong}`,
                                background: 'linear-gradient(135deg, rgba(9,13,14,0.96) 0%, rgba(94,128,127,0.18) 100%)',
                                backdropFilter: 'blur(16px)',
                                boxShadow: '0 20px 50px rgba(0,0,0,0.28)',
                            }}
                        >
                            <Stack direction="row" spacing={1.25} alignItems="center" minWidth={0}>
                                <Box
                                    component="img"
                                    src={bailaLogo}
                                    alt="Baila"
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 2.5,
                                        objectFit: 'cover',
                                        bgcolor: checkoutPalette.primary,
                                        border: `1px solid ${checkoutPalette.border}`,
                                        flexShrink: 0,
                                    }}
                                />
                                <Box minWidth={0}>
                                    <Typography fontWeight={900} letterSpacing={0.5}>
                                        BAILA
                                    </Typography>
                                    <Typography variant="body2" color={checkoutPalette.textSoft} noWrap>
                                        In der App geht es besser. Öffne dieses Event direkt dort.
                                    </Typography>
                                </Box>
                            </Stack>

                            <Button
                                variant="contained"
                                href={appOpenHref}
                                endIcon={<OpenInNewRoundedIcon />}
                                sx={{
                                    flexShrink: 0,
                                    minWidth: 118,
                                    borderRadius: 3,
                                    py: 1.1,
                                    px: 2,
                                    textTransform: 'none',
                                    fontWeight: 900,
                                    letterSpacing: 0.4,
                                    color: checkoutPalette.primary,
                                    background: checkoutPalette.secondary,
                                    boxShadow: 'none',
                                    '&:hover': {
                                        background: 'rgba(255,255,255,0.92)',
                                        boxShadow: 'none',
                                    },
                                }}
                            >
                                App öffnen
                            </Button>
                        </Box>
                    </Box>
                ) : null}

                <Box
                    sx={{
                        borderRadius: 6,
                        overflow: 'hidden',
                        border: `1px solid ${checkoutPalette.border}`,
                        background: checkoutPalette.heroGradient,
                    }}
                >
                    <Stack direction={{ xs: 'column', lg: 'row' }}>
                        <Box
                            sx={{
                                flex: 1.2,
                                minHeight: { xs: 220, md: 420 },
                                backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.72) 100%), url(${event.eventImage})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                p: { xs: 2, md: 5 },
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-end',
                            }}
                        >
                            {!mobileAppStrip ? (
                                <Typography variant="h2" fontWeight={900} lineHeight={1.02}>
                                    {event.eventName}
                                </Typography>
                            ) : null}
                        </Box>

                        <Box
                            sx={{
                                flex: 1,
                                p: { xs: 3, md: 4 },
                                background: checkoutPalette.glassSurface,
                                backdropFilter: 'blur(18px)',
                            }}
                        >
                            <Stack spacing={2.5}>
                                {mobileAppStrip ? (
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: '2rem',
                                                lineHeight: 1.02,
                                                fontWeight: 900,
                                                letterSpacing: '-0.03em',
                                                textTransform: 'uppercase',
                                            }}
                                        >
                                            {event.eventName}
                                        </Typography>
                                        <Typography color={checkoutPalette.textMuted} mt={1}>
                                            {event.eventVenueName || event.eventAddress || 'Veranstaltungsort folgt'}
                                        </Typography>
                                    </Box>
                                ) : null}
                                {!mobileAppStrip ? (
                                    <Typography variant="h5" fontWeight={800}>
                                        {eventDateLabel}
                                    </Typography>
                                ) : null}
                                {!mobileAppStrip ? (
                                    <Typography color={checkoutPalette.textMuted}>
                                        {event.eventVenueName || event.eventAddress || 'Veranstaltungsort folgt'}
                                    </Typography>
                                ) : null}
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {event.genre ? <Chip label={event.genre} sx={{ bgcolor: checkoutPalette.chipSurface, color: checkoutPalette.secondary }} /> : null}
                                    {selectedCount > 0 ? (
                                        <Chip
                                            label={`${selectedCount} Ticket${selectedCount > 1 ? 's' : ''} ausgewählt`}
                                            sx={{ bgcolor: checkoutPalette.accentSurface, color: checkoutPalette.secondary }}
                                        />
                                    ) : null}
                                    {mobileAppStrip && typeof event.attendeeCount === 'number' && event.attendeeCount > 0 ? (
                                        <Chip
                                            label={`${event.attendeeCount}+ gehen hin`}
                                            sx={{
                                                bgcolor: 'rgba(255,255,255,0.12)',
                                                color: checkoutPalette.secondary,
                                                border: '1px solid rgba(255,255,255,0.2)',
                                                fontWeight: 700,
                                            }}
                                        />
                                    ) : null}
                                    {mobileAppStrip && showTicketsLeftBadge ? (
                                        <Chip
                                            label={`${ticketsLeftBadgeCount} Tickets übrig`}
                                            sx={{
                                                bgcolor: 'rgba(255, 173, 10, 0.18)',
                                                color: checkoutPalette.secondary,
                                                border: '1px solid rgba(255, 173, 10, 0.35)',
                                                fontWeight: 700,
                                            }}
                                        />
                                    ) : null}
                                </Stack>
                                {mobileAppStrip ? (
                                    <Stack spacing={1.1}>
                                        <Stack direction="row" spacing={1.2} alignItems="center">
                                            <FmdGoodRoundedIcon sx={{ color: checkoutPalette.textSoft, fontSize: 20 }} />
                                            <Typography color={checkoutPalette.textMuted}>
                                                {mobileAddressLabel}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1.2} alignItems="center">
                                            <CalendarMonthRoundedIcon sx={{ color: checkoutPalette.textSoft, fontSize: 20 }} />
                                            <Typography color={checkoutPalette.textMuted}>
                                                {mobileDateLabel}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1.2} alignItems="center">
                                            <AccessTimeRoundedIcon sx={{ color: checkoutPalette.textSoft, fontSize: 20 }} />
                                            <Typography color={checkoutPalette.textMuted}>
                                                {mobileTimeLabel}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                ) : null}
                                <Divider sx={{ borderColor: checkoutPalette.border }} />
                            </Stack>
                        </Box>
                    </Stack>
                </Box>

                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="flex-start">
                    <Box sx={{ flex: 1.2, width: '100%' }}>
                        {desktopPaymentPreparing ? (
                            <Box
                                sx={{
                                    borderRadius: 5,
                                    border: `1px solid ${checkoutPalette.border}`,
                                    bgcolor: checkoutPalette.panelSurface,
                                    minHeight: 420,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Stack spacing={2} alignItems="center">
                                    <CircularProgress sx={{ color: checkoutPalette.secondary }} />
                                    <Typography color={checkoutPalette.textSoft} fontWeight={600}>
                                        Zahlungsformular wird vorbereitet...
                                    </Typography>
                                </Stack>
                            </Box>
                        ) : desktopPaymentOpen ? (
                            <Box
                                ref={checkoutRef}
                                sx={{
                                    borderRadius: 5,
                                    border: `1px solid ${checkoutPalette.border}`,
                                    bgcolor: checkoutPalette.panelSurface,
                                    p: { xs: 2.5, md: 3 },
                                }}
                            >
                                <Stack spacing={2.5}>
                                    <Typography variant="h5" fontWeight={800}>
                                        Zahlung
                                    </Typography>
                                    {isDev() ? (
                                        <Typography color={checkoutPalette.textSoft}>
                                            Dieser Kauf ist jetzt mit der Firebase-UID <strong>{currentUser?.uid}</strong> verknüpft.
                                        </Typography>
                                    ) : null}
                                    <PaymentStep
                                        clientSecret={paymentSheet!.paymentIntent!}
                                        stripePromise={stripePromise!}
                                        totalAmount={paymentTotal}
                                        successUrl={successUrl}
                                        onSuccess={() => {
                                            setWorkflowState('checkout_success');
                                            navigate(`/events/${eventId}/success`);
                                        }}
                                        onError={(message) => {
                                            setWorkflowState('checkout_failed');
                                            setCheckoutError(message);
                                        }}
                                    />
                                </Stack>
                            </Box>
                        ) : (
                            <>
                                <Box
                                    sx={{
                                        borderRadius: 5,
                                        border: `1px solid ${checkoutPalette.border}`,
                                        bgcolor: checkoutPalette.panelSurface,
                                        p: { xs: 2.5, md: 3 },
                                    }}
                                >
                                    <Stack spacing={2.5}>
                                        <Box>
                                            {mobileAppStrip ? (
                                                <Stack direction="row" spacing={1.1} alignItems="flex-start">
                                                    <LocalActivityOutlinedIcon sx={{ color: checkoutPalette.secondary, mt: 0.15, fontSize: 18 }} />
                                                    <Box>
                                                        <Typography fontWeight={800} fontSize="1.1rem">
                                                            Wähle dein Ticket
                                                        </Typography>
                                                        <Typography color={checkoutPalette.textSoft} mt={0.25}>
                                                            Sichere dir jetzt deinen Platz
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            ) : (
                                                <>
                                                    <Typography variant="h4" fontWeight={800}>
                                                        Wähle deine Tickets
                                                    </Typography>
                                                    <Typography color={checkoutPalette.textSoft} mt={1}>
                                                        Zum Durchsehen ist kein Konto nötig. Der Kauf wird erst nach deiner gewählten Anmeldung einem Konto zugeordnet.
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>

                                        {availableTickets.length === 0 ? (
                                            <Alert severity="info">Für dieses Event sind aktuell keine im Web kaufbaren Tickets verfügbar.</Alert>
                                        ) : (
                                            availableTickets.map((ticket) => {
                                                const quantity = selectedQuantities[ticket.eventTicketId ?? ''] ?? 0;
                                                const ticketId = ticket.eventTicketId ?? '';
                                                const ticketPreviewPrice = ticketPreviewTotals[ticketId];
                                                const ticketPrice = ticketPreviewPrice;
                                                const isRecommended = Boolean(recommendedTicketId && ticketId === recommendedTicketId);
                                                const isDetailsOpen = Boolean(openTicketDetails[ticketId]);
                                                const availabilityCount = typeof ticket.ticketsAvailable === 'number'
                                                    ? ticket.ticketsAvailable
                                                    : 0;
                                                const lowStock = availabilityCount > 0 && availabilityCount <= 10;
                                                const availabilityLabel = lowStock
                                                    ? `Nur ${availabilityCount} übrig`
                                                    : mobileAppStrip
                                                        ? `Verfügbar: ${availabilityCount}`
                                                        : `Verfügbar: ${availabilityCount}`;
                                                const urgencyLabel = getRemainingTimeLabel(ticket.ticketBuyableUntil);
                                                const maxQuantity = ticketSelectionMaxById[ticketId] ?? 5;
                                                const canIncrease = quantity < maxQuantity;

                                                return (
                                                    <Box
                                                        key={ticketId}
                                                        sx={{
                                                            borderRadius: 4,
                                                            border: `1px solid ${checkoutPalette.border}`,
                                                            px: 2.5,
                                                            py: 2,
                                                            background: mobileAppStrip
                                                                ? '#222222'
                                                                : quantity > 0
                                                                    ? 'rgba(255,255,255,0.05)'
                                                                    : 'rgba(255,255,255,0.02)',
                                                        }}
                                                    >
                                                        <Stack
                                                            direction={{ xs: 'row', sm: 'row' }}
                                                            spacing={2}
                                                            justifyContent="space-between"
                                                            alignItems="flex-start"
                                                            onClick={canIncrease ? () => updateQuantity(ticketId, quantity + 1) : undefined}
                                                            sx={canIncrease ? { cursor: 'pointer' } : undefined}
                                                        >
                                                            <Box>
                                                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                                                    <Typography variant="h6" fontWeight={800}>
                                                                        {ticket.ticketTypeName}
                                                                    </Typography>
                                                                    {mobileAppStrip && isRecommended ? (
                                                                        <Chip
                                                                            size="small"
                                                                            label="Beliebt"
                                                                            sx={{
                                                                                bgcolor: 'rgba(0, 230, 118, 0.18)',
                                                                                color: '#00E676',
                                                                                border: '1px solid rgba(0, 230, 118, 0.45)',
                                                                                fontWeight: 700,
                                                                            }}
                                                                        />
                                                                    ) : null}
                                                                </Stack>
                                                                <Typography color={mobileAppStrip ? 'rgba(255,255,255,0.75)' : checkoutPalette.textMuted} mt={0.75} fontWeight={600}>
                                                                    {typeof ticketPrice !== 'number' ? (
                                                                        <CircularProgress
                                                                            size={14}
                                                                            sx={{
                                                                                color: mobileAppStrip ? 'rgba(255,255,255,0.75)' : checkoutPalette.textMuted,
                                                                                verticalAlign: 'middle',
                                                                            }}
                                                                        />
                                                                    ) : formatCurrency(ticketPrice)}
                                                                </Typography>
                                                                <Stack
                                                                    direction={mobileAppStrip ? 'column' : 'row'}
                                                                    spacing={mobileAppStrip ? 0.25 : 1}
                                                                    mt={0.4}
                                                                >
                                                                    <Typography
                                                                        color={
                                                                            mobileAppStrip
                                                                                ? (lowStock ? 'rgba(255, 173, 10, 0.95)' : 'rgba(255,255,255,0.72)')
                                                                                : checkoutPalette.textMuted
                                                                        }
                                                                        fontWeight={600}
                                                                    >
                                                                        {availabilityLabel}
                                                                    </Typography>
                                                                    {urgencyLabel ? (
                                                                        <Typography
                                                                            color="rgba(255, 173, 10, 0.95)"
                                                                            fontWeight={600}
                                                                        >
                                                                            {urgencyLabel}
                                                                        </Typography>
                                                                    ) : null}
                                                                </Stack>
                                                                {ticket.ticketTypeDescription ? (
                                                                    <Box mt={mobileAppStrip ? 0.8 : 1.2}>
                                                                        <Button
                                                                            variant="text"
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                setOpenTicketDetails((previous) => ({
                                                                                    ...previous,
                                                                                    [ticketId]: !previous[ticketId],
                                                                                }));
                                                                            }}
                                                                            sx={{
                                                                                minWidth: 0,
                                                                                px: 0,
                                                                                py: 0,
                                                                                textTransform: 'none',
                                                                                color: mobileAppStrip ? 'rgba(255,255,255,0.75)' : checkoutPalette.textSoft,
                                                                                fontWeight: 500,
                                                                            }}
                                                                        >
                                                                            {mobileAppStrip
                                                                                ? `Details ${isDetailsOpen ? '▴' : '▾'}`
                                                                                : (isDetailsOpen ? 'Details ausblenden' : 'Details anzeigen')}
                                                                        </Button>
                                                                        {isDetailsOpen ? (
                                                                            <Typography color={mobileAppStrip ? 'rgba(255,255,255,0.72)' : checkoutPalette.textMuted} mt={0.6}>
                                                                                {ticket.ticketTypeDescription}
                                                                            </Typography>
                                                                        ) : null}
                                                                    </Box>
                                                                ) : null}
                                                            </Box>

                                                            {mobileAppStrip ? (
                                                                quantity === 0 ? (
                                                                    <Button
                                                                        variant="outlined"
                                                                        onClick={(event) => {
                                                                            event.stopPropagation();
                                                                            updateQuantity(ticketId, 1);
                                                                        }}
                                                                        disabled={!canIncrease}
                                                                        sx={{
                                                                            flexShrink: 0,
                                                                            minWidth: 108,
                                                                            borderRadius: 999,
                                                                            px: 1.4,
                                                                            py: 0.55,
                                                                            textTransform: 'none',
                                                                            color: checkoutPalette.secondary,
                                                                            borderColor: 'rgba(255,255,255,0.18)',
                                                                            bgcolor: 'rgba(255,255,255,0.03)',
                                                                        }}
                                                                    >
                                                                        <Stack direction="row" spacing={0.8} alignItems="center">
                                                                            <Box
                                                                                sx={{
                                                                                    width: 18,
                                                                                    height: 18,
                                                                                    borderRadius: '50%',
                                                                                    bgcolor: checkoutPalette.accent,
                                                                                    color: checkoutPalette.primary,
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    fontSize: 14,
                                                                                    fontWeight: 900,
                                                                                }}
                                                                            >
                                                                                +
                                                                            </Box>
                                                                            <Box component="span" sx={{ fontWeight: 700 }}>
                                                                                Hinzufügen
                                                                            </Box>
                                                                        </Stack>
                                                                    </Button>
                                                                ) : (
                                                                    <Stack direction="row" spacing={0.4} alignItems="center" justifyContent="flex-end" sx={{ flexShrink: 0 }}>
                                                                        <Button
                                                                            variant="text"
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                updateQuantity(ticketId, quantity - 1);
                                                                            }}
                                                                            sx={{
                                                                                minWidth: 28,
                                                                                px: 0.2,
                                                                                color: checkoutPalette.secondary,
                                                                                fontSize: 28,
                                                                                fontWeight: 400,
                                                                            }}
                                                                        >
                                                                            -
                                                                        </Button>
                                                                        <Box sx={{ minWidth: 20, textAlign: 'center', fontSize: 22, fontWeight: 700 }}>
                                                                            {quantity}
                                                                        </Box>
                                                                        <Button
                                                                            variant="text"
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                updateQuantity(ticketId, quantity + 1);
                                                                            }}
                                                                            disabled={!canIncrease}
                                                                            sx={{
                                                                                minWidth: 28,
                                                                                px: 0.2,
                                                                                color: checkoutPalette.secondary,
                                                                                fontSize: 28,
                                                                                fontWeight: 400,
                                                                            }}
                                                                        >
                                                                            +
                                                                        </Button>
                                                                    </Stack>
                                                                )
                                                            ) : (
                                                                quantity === 0 ? (
                                                                    <Button
                                                                        variant="outlined"
                                                                        onClick={(event) => {
                                                                            event.stopPropagation();
                                                                            updateQuantity(ticketId, 1);
                                                                        }}
                                                                        disabled={!canIncrease}
                                                                        sx={{
                                                                            flexShrink: 0,
                                                                            minWidth: 128,
                                                                            borderRadius: 999,
                                                                            px: 1.5,
                                                                            py: 0.6,
                                                                            textTransform: 'none',
                                                                            color: checkoutPalette.secondary,
                                                                            borderColor: 'rgba(255,255,255,0.18)',
                                                                            bgcolor: 'rgba(255,255,255,0.03)',
                                                                        }}
                                                                    >
                                                                        <Stack direction="row" spacing={0.8} alignItems="center">
                                                                            <Box
                                                                                sx={{
                                                                                    width: 18,
                                                                                    height: 18,
                                                                                    borderRadius: '50%',
                                                                                    bgcolor: checkoutPalette.accent,
                                                                                    color: checkoutPalette.primary,
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    fontSize: 14,
                                                                                    fontWeight: 900,
                                                                                }}
                                                                            >
                                                                                +
                                                                            </Box>
                                                                            <Box component="span" sx={{ fontWeight: 700 }}>
                                                                                Hinzufügen
                                                                            </Box>
                                                                        </Stack>
                                                                    </Button>
                                                                ) : (
                                                                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" sx={{ flexShrink: 0 }}>
                                                                        <Button
                                                                            variant="text"
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                updateQuantity(ticketId, quantity - 1);
                                                                            }}
                                                                            sx={{
                                                                                minWidth: 28,
                                                                                px: 0.2,
                                                                                color: checkoutPalette.secondary,
                                                                                fontSize: 28,
                                                                                fontWeight: 400,
                                                                            }}
                                                                        >
                                                                            -
                                                                        </Button>
                                                                        <Box
                                                                            sx={{
                                                                                minWidth: 24,
                                                                                textAlign: 'center',
                                                                                fontSize: 22,
                                                                                fontWeight: 700,
                                                                            }}
                                                                        >
                                                                            {quantity}
                                                                        </Box>
                                                                        <Button
                                                                            variant="text"
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                updateQuantity(ticketId, quantity + 1);
                                                                            }}
                                                                            disabled={!canIncrease}
                                                                            sx={{
                                                                                minWidth: 28,
                                                                                px: 0.2,
                                                                                color: checkoutPalette.secondary,
                                                                                fontSize: 28,
                                                                                fontWeight: 400,
                                                                            }}
                                                                        >
                                                                            +
                                                                        </Button>
                                                                    </Stack>
                                                                )
                                                            )}
                                                        </Stack>
                                                    </Box>
                                                );
                                            })
                                        )}

                                        {checkoutError ? <Alert severity="error">{checkoutError}</Alert> : null}
                                    </Stack>
                                </Box>

                                {eventDescription ? (
                                    <Box
                                        sx={{
                                            mt: 3,
                                            borderRadius: 5,
                                            border: `1px solid ${checkoutPalette.border}`,
                                            bgcolor: checkoutPalette.panelSurface,
                                            p: { xs: 2.5, md: 3 },
                                        }}
                                    >
                                        <Stack spacing={2}>
                                            <Box>
                                                <Typography variant="h5" fontWeight={800}>
                                                    Beschreibung
                                                </Typography>
                                            </Box>

                                            <Typography
                                                color={checkoutPalette.textMuted}
                                                sx={{
                                                    whiteSpace: 'pre-line',
                                                    wordBreak: 'break-word',
                                                    ...(descriptionExpanded ? {} : {
                                                        display: '-webkit-box',
                                                        WebkitBoxOrient: 'vertical',
                                                        WebkitLineClamp: 4,
                                                        overflow: 'hidden',
                                                    }),
                                                }}
                                            >
                                                {eventDescription}
                                            </Typography>

                                            {hasLongDescription ? (
                                                <Button
                                                    variant="text"
                                                    onClick={() => setDescriptionExpanded((previous) => !previous)}
                                                    sx={{
                                                        alignSelf: 'flex-start',
                                                        px: 0,
                                                        minWidth: 0,
                                                        textTransform: 'none',
                                                        fontWeight: 800,
                                                        color: checkoutPalette.accent,
                                                    }}
                                                >
                                                    {descriptionExpanded ? 'Weniger anzeigen' : 'Mehr lesen'}
                                                </Button>
                                            ) : null}
                                        </Stack>
                                    </Box>
                                ) : null}
                            </>
                        )}
                    </Box>

                    <Box sx={{ flex: 0.8, width: '100%' }}>
                        <Stack spacing={3}>
                            {!mobileAppStrip ? (
                                <Box
                                    sx={{
                                        borderRadius: 5,
                                        border: `1px solid ${checkoutPalette.border}`,
                                        bgcolor: checkoutPalette.panelSurface,
                                        p: { xs: 2.5, md: 3 },
                                        position: paymentSheet?.paymentIntent ? 'static' : { lg: 'sticky' },
                                        top: paymentSheet?.paymentIntent ? 'auto' : 24,
                                    }}
                                >
                                    <Stack spacing={2.5}>
                                        <Typography variant="h5" fontWeight={800}>
                                            Bestellübersicht
                                        </Typography>

                                        {selectedTicketRows.length === 0 ? (
                                            <Typography color={checkoutPalette.textSoft}>
                                                Füge Tickets hinzu, um mit dem Kauf fortzufahren.
                                            </Typography>
                                        ) : (
                                            selectedTicketRows.map((ticket) => (
                                                <Stack key={ticket.eventTicketId} direction="row" justifyContent="space-between" alignItems="center">
                                                    <Box>
                                                        <Typography fontWeight={700}>{ticket.ticketConfig?.ticketTypeName}</Typography>
                                                        <Typography variant="body2" color={checkoutPalette.textSoft}>
                                                            {ticket.quantity} × {formatCurrency(ticket.basePriceCents)}
                                                        </Typography>
                                                    </Box>
                                                    <Typography fontWeight={800}>
                                                        {formatCurrency(ticket.linePriceCents)}
                                                    </Typography>
                                                </Stack>
                                            ))
                                        )}

                                        <Divider sx={{ borderColor: checkoutPalette.border }} />

                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography color={checkoutPalette.textMuted}>Zwischensumme</Typography>
                                            <Typography fontWeight={700}>
                                                {priceDisplayPending ? <CircularProgress size={18} sx={{ color: checkoutPalette.secondary }} /> : formatCurrency(displaySubtotalCents)}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography color={checkoutPalette.textMuted}>Gebühren</Typography>
                                            <Typography fontWeight={700}>
                                                {priceDisplayPending ? <CircularProgress size={18} sx={{ color: checkoutPalette.secondary }} /> : formatCurrency(displayFeesCents)}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography color={checkoutPalette.textMuted}>Gesamt</Typography>
                                            <Typography variant="h5" fontWeight={900}>
                                                {priceDisplayPending ? <CircularProgress size={20} sx={{ color: checkoutPalette.secondary }} /> : formatCurrency(displayTotalCents)}
                                            </Typography>
                                        </Stack>

                                        {paymentQuote?.promoSummary?.applied ? (
                                            <Alert severity="success">
                                                Rabatt angewendet: {paymentQuote.promoSummary.code || paymentQuote.promoSummary.type}
                                            </Alert>
                                        ) : null}

                                        {featureFlags ? (
                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                {featureFlags.promoCodeEnabled ? <Chip size="small" label="Rabattcode verfügbar" sx={{ bgcolor: checkoutPalette.chipSurface, color: checkoutPalette.secondary }} /> : null}
                                                {featureFlags.freeCheckoutEnabled ? <Chip size="small" label="Gratiskauf verfügbar" sx={{ bgcolor: checkoutPalette.chipSurface, color: checkoutPalette.secondary }} /> : null}
                                            </Stack>
                                        ) : null}

                                        {!desktopPaymentOpen ? (
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                onClick={handleContinue}
                                                disabled={paymentLoading || eventLoading || profileLoading || availableTickets.length === 0}
                                                sx={{
                                                    borderRadius: 2.5,
                                                    py: 1.35,
                                                    textTransform: 'none',
                                                    fontWeight: 900,
                                                    fontSize: '1.05rem',
                                                    color: checkoutPalette.primary,
                                                    bgcolor: checkoutPalette.secondary,
                                                    boxShadow: 'none',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(255,255,255,0.92)',
                                                        boxShadow: 'none',
                                                    },
                                                }}
                                            >
                                                {paymentLoading || priceDisplayPending
                                                    ? <CircularProgress size={22} sx={{ color: checkoutPalette.primary }} />
                                                    : `Weiter zur Kasse · ${formatCurrency(displayTotalCents)}`}
                                            </Button>
                                        ) : null}
                                    </Stack>
                                </Box>
                            ) : null}

                        </Stack>
                    </Box>
                </Stack>
            </Stack>

            {mobileAppStrip ? (
                <Box
                    sx={{
                        position: 'fixed',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 5,
                        px: 1.5,
                        pb: 'calc(env(safe-area-inset-bottom, 0px) + 10px)',
                        pt: 1,
                        background: 'linear-gradient(180deg, rgba(6,8,9,0) 0%, rgba(6,8,9,0.72) 20%, rgba(6,8,9,0.96) 42%, rgba(6,8,9,1) 100%)',
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: 560,
                            mx: 'auto',
                            borderRadius: 4,
                            border: `1px solid ${checkoutPalette.border}`,
                            bgcolor: '#111111',
                            px: 2,
                            pt: 1.2,
                            pb: 1.5,
                            boxShadow: '0 -12px 40px rgba(0,0,0,0.42)',
                        }}
                    >
                        {selectedTicketRows.length > 0 ? (
                            <Button
                                variant="text"
                                onClick={() => setSummaryExpanded((previous) => !previous)}
                                sx={{
                                    alignSelf: 'flex-end',
                                    display: 'flex',
                                    ml: 'auto',
                                    minWidth: 0,
                                    px: 0,
                                    py: 0,
                                    textTransform: 'none',
                                    color: 'rgba(255,255,255,0.72)',
                                    fontSize: '0.78rem',
                                    fontWeight: 700,
                                }}
                            >
                                {summaryExpanded ? 'Details ausblenden' : 'Details anzeigen'}
                            </Button>
                        ) : null}

                        {summaryExpanded ? (
                            <Stack spacing={1.15} mt={0.75} mb={1.4}>
                                {selectedTicketRows.map((ticket) => (
                                    <Stack
                                        key={ticket.eventTicketId}
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <Typography color="rgba(255,255,255,0.8)">
                                            {ticket.quantity} × {ticket.ticketConfig?.ticketTypeName}
                                        </Typography>
                                        <Typography color="rgba(255,255,255,0.8)">
                                            {formatCurrency(ticket.linePriceCents)}
                                        </Typography>
                                    </Stack>
                                ))}

                                {promoDiscountCents > 0 ? (
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography color="rgba(255,255,255,0.8)">- Rabatt</Typography>
                                        <Typography color="rgba(255,255,255,0.8)">
                                            -{formatCurrency(promoDiscountCents)}
                                        </Typography>
                                    </Stack>
                                ) : null}

                                {selectedTicketRows.length > 0 ? (
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography color="rgba(255,255,255,0.8)">Zwischensumme</Typography>
                                        <Typography color="rgba(255,255,255,0.8)">
                                            {publicQuotePending ? '...' : formatCurrency(displaySubtotalCents)}
                                        </Typography>
                                    </Stack>
                                ) : null}

                                {freeTicketCount > 0 ? (
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography color="rgba(255,255,255,0.8)">Gratis Tickets</Typography>
                                        <Typography color="rgba(255,255,255,0.8)">
                                            {freeTicketCount}
                                        </Typography>
                                    </Stack>
                                ) : null}

                                {displayFeesCents > 0 ? (
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography color="rgba(255,255,255,0.8)">Gebühren</Typography>
                                        <Typography color="rgba(255,255,255,0.8)">
                                            {publicQuotePending ? '...' : formatCurrency(displayFeesCents)}
                                        </Typography>
                                    </Stack>
                                ) : null}
                            </Stack>
                        ) : null}

                        <Stack direction="row" justifyContent="space-between" alignItems="flex-end" mb={1.3}>
                            <Box>
                                <Typography fontWeight={900} fontSize="1.6rem">
                                    Gesamt
                                </Typography>
                                <Typography color="rgba(255,255,255,0.62)" fontSize="0.82rem" mt={0.15}>
                                    inkl. Gebühren
                                </Typography>
                            </Box>
                            <Typography
                                fontWeight={900}
                                fontSize="1.8rem"
                                color={checkoutPalette.accent}
                                lineHeight={1}
                            >
                                {publicQuotePending ? (
                                    <CircularProgress size={18} sx={{ color: checkoutPalette.accent }} />
                                ) : (
                                    formatCurrency(displayTotalCents)
                                )}
                            </Typography>
                        </Stack>

                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleMobilePrimaryAction}
                            disabled={
                                paymentLoading
                                || eventLoading
                                || profileLoading
                                || availableTickets.length === 0
                                || selectedTicketRows.length === 0
                            }
                            sx={{
                                borderRadius: 2.5,
                                py: 1.35,
                                textTransform: 'none',
                                fontWeight: 900,
                                fontSize: '1.05rem',
                                color: checkoutPalette.primary,
                                bgcolor: checkoutPalette.secondary,
                                boxShadow: 'none',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.92)',
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            {paymentLoading ? (
                                <CircularProgress size={22} sx={{ color: checkoutPalette.primary }} />
                            ) : publicQuotePending ? (
                                <CircularProgress size={22} sx={{ color: checkoutPalette.primary }} />
                            ) : paymentSheet?.paymentIntent ? (
                                `Zum Zahlungsformular · ${formatCurrency(displayTotalCents)}`
                            ) : (
                                `Weiter zur Zahlung · ${formatCurrency(displayTotalCents)}`
                            )}
                        </Button>

                        <Typography
                            color="rgba(255,255,255,0.5)"
                            textAlign="center"
                            fontSize="0.76rem"
                            mt={1.1}
                        >
                            Sichere Zahlung · Sofortige Bestätigung · Hilfe
                        </Typography>
                    </Box>
                </Box>
            ) : null}

            <Dialog
                open={authDialogOpen}
                onClose={authBusy ? undefined : () => setAuthDialogOpen(false)}
                fullScreen={fullScreenDialog}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        background: 'radial-gradient(circle at top, rgba(240,138,93,0.14) 0%, rgba(6,8,9,0.96) 34%), linear-gradient(180deg, rgba(18,14,14,0.98) 0%, #060809 100%)',
                        color: checkoutPalette.secondary,
                        borderRadius: fullScreenDialog ? 0 : 5,
                    },
                }}
            >
                <DialogContent sx={{ p: { xs: 2, md: 3.5 } }}>
                    <Stack spacing={3.25}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: { xs: '2.2rem', md: '2.7rem' },
                                        lineHeight: { xs: 1.02, md: 1.04 },
                                        fontWeight: 900,
                                        letterSpacing: '-0.04em',
                                        textTransform: mobileAppStrip ? 'uppercase' : 'none',
                                        whiteSpace: 'pre-line',
                                        maxWidth: 520,
                                    }}
                                >
                                    {authDialogTitle}
                                </Typography>
                                <Typography color={checkoutPalette.textSoft} mt={1.15} fontSize="1.02rem">
                                    {authDialogSubtitle}
                                </Typography>
                            </Box>
                            <IconButton
                                onClick={handleAuthBack}
                                disabled={authBusy}
                                sx={{
                                    color: checkoutPalette.secondary,
                                    alignSelf: 'flex-start',
                                    mt: 0.4,
                                }}
                            >
                                <ArrowBackRoundedIcon />
                            </IconButton>
                        </Stack>

                        {authError ? <Alert severity="error">{authError}</Alert> : null}

                        {(workflowState === 'email_entry' || workflowState === 'email_lookup_pending') ? (
                            <Stack spacing={2} sx={{ mx: { xs: 3, sm: 0 } }}>
                                <TextField
                                    fullWidth
                                    placeholder="E-Mail"
                                    value={emailAuthForm.email}
                                    onChange={(event) => setEmailAuthForm((previous) => ({ ...previous, email: event.target.value }))}
                                    inputProps={{ autoCapitalize: 'none' }}
                                    sx={authFieldSx}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleEmailEntrySubmit}
                                    disabled={authBusy}
                                    sx={authPrimaryButtonSx}
                                >
                                    {authBusy ? <CircularProgress size={22} color="inherit" /> : 'Weiter zur Kasse'}
                                </Button>
                                <Button
                                    variant="text"
                                    onClick={() => {
                                        setAuthError(null);
                                        setWorkflowState('guest_contact_collecting');
                                    }}
                                    disabled={authBusy}
                                    sx={authSecondaryTextButtonSx}
                                >
                                    Als Gast fortfahren
                                </Button>
                            </Stack>
                        ) : null}

                        {(workflowState === 'email_login' || workflowState === 'email_register' || workflowState === 'email_auth_pending') ? (
                            <Stack spacing={2} sx={{ mx: { xs: 3, sm: 0 } }}>
                                <Stack direction="row" spacing={2.5} alignItems="flex-start">
                                    <Box>
                                        <Typography
                                            color={emailMode === 'register' ? checkoutPalette.secondary : '#A1A1AA'}
                                            fontSize="1.45rem"
                                            fontWeight={800}
                                        >
                                            Konto erstellen
                                        </Typography>
                                        {emailMode === 'register' ? (
                                            <Box mt={0.5} height={3} borderRadius={999} bgcolor={checkoutPalette.secondary} />
                                        ) : null}
                                    </Box>
                                    <Box>
                                        <Typography
                                            color={emailMode === 'login' ? checkoutPalette.secondary : '#A1A1AA'}
                                            fontSize="1.45rem"
                                            fontWeight={800}
                                        >
                                            Anmelden
                                        </Typography>
                                        {emailMode === 'login' ? (
                                            <Box mt={0.5} height={3} borderRadius={999} bgcolor={checkoutPalette.secondary} />
                                        ) : null}
                                    </Box>
                                </Stack>
                                <TextField
                                    fullWidth
                                    placeholder="E-Mail"
                                    value={emailAuthForm.email}
                                    onChange={(event) => setEmailAuthForm((previous) => ({ ...previous, email: event.target.value }))}
                                    inputProps={{ autoCapitalize: 'none' }}
                                    sx={authFieldSx}
                                />
                                {emailMode === 'register' ? (
                                    <TextField
                                        fullWidth
                                        placeholder="E-Mail bestätigen"
                                        value={emailAuthForm.emailConfirmation}
                                        onChange={(event) => setEmailAuthForm((previous) => ({ ...previous, emailConfirmation: event.target.value }))}
                                        inputProps={{ autoCapitalize: 'none' }}
                                        sx={authFieldSx}
                                    />
                                ) : null}
                                <TextField
                                    fullWidth
                                    placeholder="Passwort"
                                    type="password"
                                    value={emailAuthForm.password}
                                    onChange={(event) => setEmailAuthForm((previous) => ({ ...previous, password: event.target.value }))}
                                    sx={authFieldSx}
                                />
                                {emailMode === 'register' ? (
                                    <TextField
                                        fullWidth
                                        placeholder="Passwort bestätigen"
                                        type="password"
                                        value={emailAuthForm.confirmPassword}
                                        onChange={(event) => setEmailAuthForm((previous) => ({ ...previous, confirmPassword: event.target.value }))}
                                        sx={authFieldSx}
                                    />
                                ) : null}
                                {emailMode === 'login' ? (
                                    <Button
                                        variant="text"
                                        disabled
                                        sx={authSecondaryTextButtonSx}
                                    >
                                        Passwort vergessen?
                                    </Button>
                                ) : null}
                                <Button
                                    variant="contained"
                                    onClick={handleEmailAuthSubmit}
                                    disabled={authBusy}
                                    sx={authPrimaryButtonSx}
                                >
                                    {authBusy
                                        ? <CircularProgress size={22} color="inherit" />
                                        : emailMode === 'login'
                                            ? 'Anmelden und fortfahren'
                                            : 'Konto erstellen und fortfahren'}
                                </Button>
                            </Stack>
                        ) : null}

                        {workflowState === 'guest_contact_collecting' || workflowState === 'guest_auth_creating' ? (
                            <Stack spacing={2} sx={{ mx: { xs: 3, sm: 0 } }}>
                                <Typography
                                    sx={{
                                        fontSize: { xs: '2rem', md: '2.4rem' },
                                        lineHeight: 1.02,
                                        fontWeight: 900,
                                        letterSpacing: '-0.04em',
                                        textTransform: mobileAppStrip ? 'uppercase' : 'none',
                                    }}
                                >
                                    Gastkauf
                                </Typography>
                                <Typography color={checkoutPalette.textSoft}>
                                    Bevor ein anonymer Firebase-Benutzer erstellt wird, benötigen wir deine Kontaktdaten.
                                </Typography>
                                <TextField
                                    fullWidth
                                    placeholder="E-Mail"
                                    value={guestForm.email}
                                    onChange={(event) => setGuestForm((previous) => ({ ...previous, email: event.target.value }))}
                                    sx={authFieldSx}
                                />
                                <TextField
                                    fullWidth
                                    placeholder="Vorname"
                                    value={guestForm.name}
                                    onChange={(event) => setGuestForm((previous) => ({ ...previous, name: event.target.value }))}
                                    sx={authFieldSx}
                                />
                                <TextField
                                    fullWidth
                                    placeholder="Nachname"
                                    value={guestForm.lastName}
                                    onChange={(event) => setGuestForm((previous) => ({ ...previous, lastName: event.target.value }))}
                                    sx={authFieldSx}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleGuestSubmit}
                                    disabled={authBusy}
                                    sx={authPrimaryButtonSx}
                                >
                                    {authBusy ? <CircularProgress size={22} color="inherit" /> : 'Kontaktdaten speichern und fortfahren'}
                                </Button>
                            </Stack>
                        ) : null}

                        {workflowState === 'registered_missing_contact' ? (
                            <Stack spacing={2} sx={{ mx: { xs: 3, sm: 0 } }}>
                                <Typography
                                    sx={{
                                        fontSize: { xs: '2rem', md: '2.4rem' },
                                        lineHeight: 1.02,
                                        fontWeight: 900,
                                        letterSpacing: '-0.04em',
                                        textTransform: mobileAppStrip ? 'uppercase' : 'none',
                                    }}
                                >
                                    Vervollständige dein Profil
                                </Typography>
                                <Typography color={checkoutPalette.textSoft}>
                                    Vor der Zahlung benötigen wir nur noch deinen Vor- und Nachnamen.
                                </Typography>
                                <TextField
                                    fullWidth
                                    placeholder="E-Mail"
                                    value={contactForm.email}
                                    InputProps={{ readOnly: true }}
                                    sx={authFieldSx}
                                />
                                <TextField
                                    fullWidth
                                    placeholder="Vorname"
                                    value={contactForm.name}
                                    onChange={(event) => setContactForm((previous) => ({ ...previous, name: event.target.value }))}
                                    sx={authFieldSx}
                                />
                                <TextField
                                    fullWidth
                                    placeholder="Nachname"
                                    value={contactForm.lastName}
                                    onChange={(event) => setContactForm((previous) => ({ ...previous, lastName: event.target.value }))}
                                    sx={authFieldSx}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleRegisteredContactSubmit}
                                    disabled={authBusy}
                                    sx={authPrimaryButtonSx}
                                >
                                    {authBusy ? <CircularProgress size={22} color="inherit" /> : 'Speichern und fortfahren'}
                                </Button>
                            </Stack>
                        ) : null}

                        {workflowState === 'email_entry' || workflowState === 'email_lookup_pending' ? (
                            <Box
                                sx={{
                                    borderRadius: 4,
                                    bgcolor: 'rgba(0, 230, 118, 0.08)',
                                    border: '1px solid rgba(0, 230, 118, 0.16)',
                                    p: 2,
                                    mx: { xs: 3, sm: 0 },
                                }}
                            >
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <CheckCircleRoundedIcon sx={{ color: checkoutPalette.success }} />
                                    <Typography fontWeight={700}>
                                        Kaufanfragen bleiben durchgehend authentifiziert.
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" color={checkoutPalette.textSoft} mt={1}>
                                    Das Browsen im Web bleibt ohne Anmeldung. Der Gastkauf erstellt genau einen anonymen Firebase-Benutzer erst nach erfolgreicher Eingabe gültiger Kontaktdaten.
                                </Typography>
                            </Box>
                        ) : null}
                    </Stack>
                </DialogContent>
            </Dialog>

            <Dialog
                open={Boolean(mobileAppStrip && paymentDialogOpen && paymentSheet?.paymentIntent && stripePromise)}
                onClose={paymentLoading ? undefined : () => setPaymentDialogOpen(false)}
                fullScreen
                fullWidth
                PaperProps={{
                    sx: {
                        background: 'radial-gradient(circle at top, rgba(240,138,93,0.14) 0%, rgba(6,8,9,0.96) 34%), linear-gradient(180deg, rgba(18,14,14,0.98) 0%, #060809 100%)',
                        color: checkoutPalette.secondary,
                    },
                }}
            >
                <DialogContent sx={{ p: 2 }}>
                    <Stack spacing={3.25}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1.5,
                                borderRadius: 4,
                                px: 2,
                                py: 1.5,
                                border: `1px solid ${checkoutPalette.borderStrong}`,
                                background: 'linear-gradient(135deg, rgba(9,13,14,0.96) 0%, rgba(94,128,127,0.18) 100%)',
                                backdropFilter: 'blur(16px)',
                                boxShadow: '0 20px 50px rgba(0,0,0,0.28)',
                            }}
                        >
                            <Stack direction="row" spacing={1.25} alignItems="center" minWidth={0}>
                                <Box
                                    component="img"
                                    src={bailaLogo}
                                    alt="Baila"
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 2.5,
                                        objectFit: 'cover',
                                        bgcolor: checkoutPalette.primary,
                                        border: `1px solid ${checkoutPalette.border}`,
                                        flexShrink: 0,
                                    }}
                                />
                                <Box minWidth={0}>
                                    <Typography fontWeight={900} letterSpacing={0.5}>
                                        BAILA
                                    </Typography>
                                    <Typography variant="body2" color={checkoutPalette.textSoft} noWrap>
                                        In der App geht es besser. Öffne dieses Event direkt dort.
                                    </Typography>
                                </Box>
                            </Stack>

                            <Button
                                variant="contained"
                                href={appOpenHref}
                                endIcon={<OpenInNewRoundedIcon />}
                                sx={{
                                    flexShrink: 0,
                                    minWidth: 118,
                                    borderRadius: 3,
                                    py: 1.1,
                                    px: 2,
                                    textTransform: 'none',
                                    fontWeight: 900,
                                    letterSpacing: 0.4,
                                    color: checkoutPalette.primary,
                                    background: checkoutPalette.secondary,
                                    boxShadow: 'none',
                                    '&:hover': {
                                        background: 'rgba(255,255,255,0.92)',
                                        boxShadow: 'none',
                                    },
                                }}
                            >
                                App öffnen
                            </Button>
                        </Box>

                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography
                                sx={{
                                    fontSize: '2.1rem',
                                    lineHeight: 1.02,
                                    fontWeight: 900,
                                    letterSpacing: '-0.04em',
                                    textTransform: 'uppercase',
                                }}
                            >
                                Zahlung
                            </Typography>
                            <IconButton
                                onClick={() => setPaymentDialogOpen(false)}
                                disabled={paymentLoading}
                                sx={{
                                    color: checkoutPalette.secondary,
                                    alignSelf: 'flex-start',
                                    mt: 0.4,
                                }}
                            >
                                <ArrowBackRoundedIcon />
                            </IconButton>
                        </Stack>

                        <Typography color={checkoutPalette.textSoft}>
                            Wähle deine Zahlungsmethode und schliesse den Kauf ab.
                        </Typography>

                        {paymentSheet?.paymentIntent && stripePromise ? (
                            <Box
                                sx={{
                                    mx: 1,
                                    borderRadius: 4,
                                    border: `1px solid ${checkoutPalette.border}`,
                                    bgcolor: checkoutPalette.panelSurface,
                                    p: 2,
                                }}
                            >
                                <PaymentStep
                                    clientSecret={paymentSheet.paymentIntent}
                                    stripePromise={stripePromise}
                                    totalAmount={paymentTotal}
                                    successUrl={successUrl}
                                    onSuccess={() => {
                                        setPaymentDialogOpen(false);
                                        setWorkflowState('checkout_success');
                                        navigate(`/events/${eventId}/success`);
                                    }}
                                    onError={(message) => {
                                        setWorkflowState('checkout_failed');
                                        setCheckoutError(message);
                                    }}
                                />
                            </Box>
                        ) : null}
                    </Stack>
                </DialogContent>
            </Dialog>
        </Box>
    );
}
